import { Button, Grid } from '@material-ui/core';
import { equals, isNil } from 'ramda';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { PageLoader } from '~components/Page';

import { getTitle } from './UnitsDialog.config';

import { appRoutes } from '~constants';
import { Alarm, PermissionName } from '~models';
import { boundActions, selectors } from '~store';
import { Dialog, DialogContent, DialogActions, DataPlaceholder, NavLink, DialogTitle } from '~ui-kit';

type GeoInfoDialogProps = {
  open: boolean;
  alarm: Alarm;
  onClose: () => void;
};

export const GeoInfoDialog = ({ open = false, alarm, onClose }: GeoInfoDialogProps) => {
  if (!open) {
    return null;
  }
  const { data: units, loading: isLoading } = useSelector(selectors.app.getUnitsSearch, equals);
  const unitDetails = units?.filter(
    item => item.buId === alarm.businessUnitId && item.unitType === alarm.unitType
  )?.[0];
  const hasNoCoordinates = isNil(unitDetails?.latitude) || isNil(unitDetails?.longitude);
  const isSelfMonitoring = useSelector(selectors.app.isSelfMonitoring);
  const { hydrantRadius } = useSelector(selectors.settings.getSelfMonitoringSettings, equals);
  const hasPermissions = useSelector(selectors.auth.hasPermissions(PermissionName.SELF_MON_SETTINGS_VIEW), equals);

  const onClickLink = useCallback(e => {
    if (hasNoCoordinates) {
      e.preventDefault();
    }
    boundActions.geography.selectBusinessUnit(unitDetails?.buId as number);
    boundActions.geography.selectUnitSearch({
      id: unitDetails?.unitId,
      unitType: unitDetails?.unitType,
      latitude: unitDetails?.latitude as number,
      longitude: unitDetails?.longitude as number,
      radius: hydrantRadius?.radius as number ?? 0,
    });
  }, [unitDetails, hydrantRadius]);

  useEffect(() => {
    boundActions.app.fetchUnitsSearchInit({
      unitId: alarm.unitId,
    });
  }, [alarm.unitId]);

  useEffect(() => {
    if (isSelfMonitoring && hasPermissions) {
      boundActions.settings.fetchHydrantRadiusSettingsInit();
    }
  }, []);

  return (
    <Dialog fullWidth maxWidth="sm" open={open}>
      <DialogTitle title={getTitle(alarm.unitId, alarm.unitType)} />
      <DialogContent>
        {isLoading ? (
          <PageLoader />
        ) : units?.length ? (
          <Grid container>
            <Grid item xs={12}>
              {getTitle(alarm.unitId, alarm.unitType)}
            </Grid>
            <Grid item xs={12}>
              Address: {alarm?.address1 || '-'}
            </Grid>
            <Grid item xs={12}>
              Latitude: {unitDetails?.latitude || '-'}
            </Grid>
            <Grid item xs={12}>
              Longitude: {unitDetails?.longitude || '-'}
            </Grid>
          </Grid>
        ) : (
          <DataPlaceholder />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Close
        </Button>
        <NavLink
          strict={false}
          style={{ display: 'flex', textAlign: 'center' }}
          to={appRoutes.geography}
          onClick={onClickLink}
        >
          <Button size="medium" color="primary" variant="contained" disabled={hasNoCoordinates}>
            Show on map
          </Button>
        </NavLink>
      </DialogActions>
    </Dialog>
  );
};
